import React, { useMemo, useState } from "react";
import { useSelector } from 'react-redux';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import moment from 'moment';
import { accessTypes } from '../../helpers/Utils';

const COLORS = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'];

// Trend Indicator Component with Tooltip
const TrendIndicator = ({ trendData, isCurrency = false }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  if (!trendData || trendData.percentage === null) return null;
  
  const isPositive = trendData.percentage >= 0;
  const color = isPositive ? '#16a34a' : '#dc2626';
  const bgColor = isPositive ? 'rgba(22, 163, 74, 0.1)' : 'rgba(220, 38, 38, 0.1)';
  const arrow = isPositive ? '↑' : '↓';
  
  const formatValue = (value) => {
    if (isCurrency) {
      return `AED ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return value.toLocaleString();
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginLeft: '1rem',
      flexShrink: 0,
      position: 'relative'
    }}>
      <div 
        style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          color: color,
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          padding: '0.25rem 0.5rem',
          backgroundColor: bgColor,
          borderRadius: '0.375rem',
          cursor: 'pointer',
          position: 'relative'
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <span>{arrow}</span>
        <span>
          {Math.abs(trendData.percentage).toFixed(1)}%
        </span>
      </div>
      {showTooltip && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: '0.5rem',
          padding: '0.75rem',
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '0.5rem',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          zIndex: 50,
          minWidth: '280px',
          whiteSpace: 'nowrap'
        }}>
          <div style={{ marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
              Current Period
            </div>
            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '0.125rem' }}>
              {formatValue(trendData.currentTotal)}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
              {trendData.currentPeriod}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
              Previous Period
            </div>
            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '0.125rem' }}>
              {formatValue(trendData.previousTotal)}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
              {trendData.previousPeriod}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TopWashers = ({ 
  dateRange, 
  dailyAnalytics, 
  previousDailyAnalytics,
  loading,
  error,
  filterBySpId = null
}) => {
  // Get data from Redux store - memoized to prevent unnecessary recalculations
  const { user } = useSelector(state => state.authUser);
  const allWashers = useSelector(state => state.providers?.washers || {}, (prev, next) => {
    return JSON.stringify(prev) === JSON.stringify(next);
  });
  const allProviders = useSelector(state => state.providers?.provider || {}, (prev, next) => {
    return JSON.stringify(prev) === JSON.stringify(next);
  });

  // Memoize date ranges
  const { currentPeriodRange, previousPeriodRange } = useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) {
      return { currentPeriodRange: null, previousPeriodRange: null };
    }

    const currentStart = moment(dateRange.startDate);
    const currentEnd = moment(dateRange.endDate);
    const periodLength = currentEnd.diff(currentStart, 'days');

    const previousStart = currentStart.clone().subtract(periodLength + 1, 'days');
    const previousEnd = currentStart.clone().subtract(1, 'days');

    return {
      currentPeriodRange: `${currentStart.format('MMM DD, YYYY')} - ${currentEnd.format('MMM DD, YYYY')}`,
      previousPeriodRange: `${previousStart.format('MMM DD, YYYY')} - ${previousEnd.format('MMM DD, YYYY')}`
    };
  }, [dateRange?.startDate, dateRange?.endDate]);

  // Memoize chart data calculation
  const { chartData, trendData } = useMemo(() => {
    if (!dailyAnalytics || dailyAnalytics.length === 0) {
      return { chartData: [], trendData: null };
    }

    // Determine which SP to filter by
    // Priority: filterBySpId prop (from SelectedServiceProvider) > user's SP ID > null (show all)
    let spUserId = filterBySpId;
    if (!spUserId) {
      const role = user?.accessType || 'superAdmin';
      const isSP = !accessTypes.includes(role);
      spUserId = isSP ? user?.uid : null;
    }

    // Aggregate data by washer from current period
    const washerBookings = {};
    let totalBookings = 0;

    dailyAnalytics.forEach(day => {
      if (day.washer) {
        Object.keys(day.washer).forEach(washerId => {
          const washer = allWashers?.[washerId];
          
          // Filter: Only include washers belonging to the specified SP
          if (spUserId && washer?.sp_id !== spUserId) {
            return; // Skip washers not belonging to this SP
          }

          const washerData = day.washer[washerId];
          if (washerData.totalBookings) {
            washerBookings[washerId] = (washerBookings[washerId] || 0) + washerData.totalBookings;
            totalBookings += washerData.totalBookings;
          }
        });
      }
    });

    // Get top 6 washers by bookings
    const sortedWashers = Object.entries(washerBookings)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);

    // Get the IDs of top 6 washers to track them in previous period
    const top6WasherIds = sortedWashers.map(([washerId]) => washerId);

    // Prepare chart data - only include active washers
    const pieData = sortedWashers
      .filter(([washerId]) => allWashers?.[washerId]?.active)
      .map(([washerId, bookings], index) => {
        const washer = allWashers?.[washerId];
        const provider = washer?.sp_id ? allProviders?.[washer.sp_id] : null;
        
        return {
          id: washerId,
          name: washer?.name || `Washer ${washerId.substring(0, 8)}`,
          washerId: washerId,
          spName: provider?.name || 'Unknown SP',
          spId: washer?.sp_id || null,
          spLogo: provider?.logo || provider?.pictures || null,
          value: bookings,
          percentage: totalBookings > 0 ? ((bookings / totalBookings) * 100).toFixed(1) : 0,
          photo: washer?.profile_image || washer?.photo || washer?.image || null,
          rank: index + 1
        };
      });

    // Calculate trend from previous period data - only for the same top 6 washers
    let trendData = null;
    if (previousDailyAnalytics && previousDailyAnalytics.length > 0) {
      let previousTotalBookings = 0;
      
      previousDailyAnalytics.forEach(day => {
        if (day.washer) {
          // Only count bookings for the same top 6 washers
          top6WasherIds.forEach(washerId => {
            const washer = allWashers?.[washerId];
            
            // Apply same SP filter for previous period
            if (spUserId && washer?.sp_id !== spUserId) {
              return; // Skip washers not belonging to this SP
            }

            if (day.washer[washerId]) {
              previousTotalBookings += day.washer[washerId].totalBookings || 0;
            }
          });
        }
      });

      const trendPercentage = previousTotalBookings > 0 
        ? ((totalBookings - previousTotalBookings) / previousTotalBookings) * 100 
        : (totalBookings > 0 ? 100 : null);
      
      trendData = {
        percentage: trendPercentage,
        currentTotal: totalBookings,
        previousTotal: previousTotalBookings,
        currentPeriod: currentPeriodRange,
        previousPeriod: previousPeriodRange
      };
      
      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('Top Washers Trend:', {
          top6Washers: top6WasherIds,
          currentTotalBookings: totalBookings,
          previousTotalBookings,
          trendPercentage,
          filterBySpId,
          spUserId,
          currentPeriodDays: dailyAnalytics.length,
          previousPeriodDays: previousDailyAnalytics.length
        });
      }
    }

    return { chartData: pieData, trendData };
  }, [dailyAnalytics, previousDailyAnalytics, allWashers, allProviders, user, filterBySpId, currentPeriodRange, previousPeriodRange]);

  // Get rank badge component
  const getRankBadge = (rank) => {
    const badges = {
      1: { icon: '🥇', color: '#FFD700', label: '1st' },
      2: { icon: '🥈', color: '#C0C0C0', label: '2nd' },
      3: { icon: '🥉', color: '#CD7F32', label: '3rd' }
    };
    
    if (rank <= 3) {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25rem',
          fontSize: '0.75rem',
          fontWeight: 600,
          padding: '0.125rem 0.375rem',
          borderRadius: '0.25rem',
          backgroundColor: `${badges[rank].color}20`,
          color: '#374151'
        }}>
          {badges[rank].icon} #{rank}
        </span>
      );
    }

    return (
      <span style={{
        fontSize: '0.75rem',
        fontWeight: 500,
        color: '#6b7280'
      }}>
        #{rank}
      </span>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
                  return (
        <div style={{
          backgroundColor: '#fff',
          padding: '12px',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          minWidth: '220px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            {data.photo ? (
              <img 
                src={data.photo} 
                alt={data.name}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '6px',
                  objectFit: 'cover',
                  border: '1px solid #e5e7eb'
                }}
              />
            ) : (
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                backgroundColor: payload[0].fill,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.875rem'
              }}>
                {data.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {getRankBadge(data.rank)}
              </div>
            </div>
          </div>
          <p style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#111827' }}>{data.name}</p>
          <div style={{ 
            borderTop: '1px solid #e5e7eb',
            paddingTop: '0.5rem',
            marginTop: '0.5rem'
          }}>
            <p style={{ margin: '2px 0', color: payload[0].fill, fontWeight: 500, fontSize: '0.875rem' }}>
              <span style={{ color: '#6b7280' }}>Bookings: </span>{data.value.toLocaleString()}
            </p>
            <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '0.875rem' }}>
              <span style={{ fontWeight: 500 }}>{data.percentage}%</span> of total
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const dateRangeLabel = dateRange?.startDate && dateRange?.endDate
    ? `${moment(dateRange.startDate).format('MMM DD, YYYY')} - ${moment(dateRange.endDate).format('MMM DD, YYYY')}`
    : 'Select date range';

  if (loading) {
    return (
      <Card style={{ paddingTop: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader 
          style={{ 
            display: 'flex', 
            flexDirection: 'row', 
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            borderBottom: '1px solid hsl(var(--border))',
            padding: '1.5rem 1.5rem 0.75rem 1.5rem',
            flexShrink: 0
          }}
        >
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            gap: '0.25rem',
            flex: 1
          }}>
            <CardTitle style={{ fontSize: '1.125rem', fontWeight: 600 }}>Top Washers</CardTitle>
            <CardDescription style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {dateRangeLabel}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent style={{ 
          padding: '1.5rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1
        }}>
          <div className="loading" />
        </CardContent>
      </Card>
    );
  }

  if (!chartData || chartData.length === 0 || !Array.isArray(chartData)) {
    return (
      <Card style={{ paddingTop: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader 
          style={{ 
            display: 'flex', 
            flexDirection: 'row', 
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            borderBottom: '1px solid hsl(var(--border))',
            padding: '1.5rem 1.5rem 0.75rem 1.5rem',
            flexShrink: 0
          }}
        >
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            gap: '0.25rem',
            flex: 1
          }}>
            <CardTitle style={{ fontSize: '1.125rem', fontWeight: 600 }}>Top Washers</CardTitle>
            <CardDescription style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {dateRangeLabel}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent style={{ 
          padding: '1.5rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          color: '#6b7280',
          fontSize: '0.875rem'
        }}>
          <svg 
            style={{ width: '48px', height: '48px', marginBottom: '1rem', opacity: 0.3 }}
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <p style={{ margin: 0, textAlign: 'center' }}>No data available for the selected date range</p>
        </CardContent>
      </Card>
    );
  }

  const totalBookings = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card style={{ paddingTop: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader 
        style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          borderBottom: '1px solid hsl(var(--border))',
          padding: '1.5rem 1.5rem 0.75rem 1.5rem',
          flexShrink: 0
        }}
      >
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          gap: '0.25rem',
          flex: 1,
          minWidth: 0
        }}>
          <CardTitle style={{ fontSize: '1.125rem', fontWeight: 600 }}>Top Washers</CardTitle>
          <CardDescription style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            {dateRangeLabel}
          </CardDescription>
        </div>
        {trendData && <TrendIndicator trendData={trendData} />}
      </CardHeader>
      <CardContent style={{ 
        padding: '1.5rem',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}>
        {/* Chart Container */}
        <div style={{ 
          width: '100%', 
          height: '300px',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ rank, percentage }) => `#${rank} (${percentage}%)`}
                outerRadius={110}
                innerRadius={60}
                fill="#8884d8"
                dataKey="value"
                paddingAngle={2}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Summary Stats */}
        <div style={{ 
          textAlign: 'center', 
          padding: '0.75rem 1rem',
          backgroundColor: '#f9fafb',
          borderRadius: '0.5rem',
          marginBottom: '1rem'
        }}>
          <p style={{ 
            color: '#374151', 
            fontSize: '0.875rem',
            margin: 0,
            fontWeight: 500
          }}>
            <span style={{ color: '#6b7280' }}>Total Bookings: </span>
            <span style={{ fontWeight: 600 }}>{totalBookings.toLocaleString()}</span>
          </p>
        </div>

        {/* Ranked List with Photos and SP Info */}
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          // marginTop: 'auto'
        }}>
          {chartData.map((entry, index) => (
            <div key={index} style={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.75rem',
              backgroundColor: index < 3 ? '#dbeafe' : '#f9fafb',
              border: index < 3 ? '1px solid #3b82f6' : '1px solid transparent',
              borderRadius: '0.5rem',
              transition: 'all 0.2s',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = index < 3 ? '#bfdbfe' : '#f3f4f6';
              e.currentTarget.style.transform = 'translateX(4px)';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = index < 3 ? '#dbeafe' : '#f9fafb';
              e.currentTarget.style.transform = 'translateX(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            >
              {/* Rank Badge */}
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '45px'
              }}>
                {getRankBadge(entry.rank)}
              </div>

              {/* Washer Photo/Avatar */}
              {entry.photo ? (
                <img 
                  src={entry.photo} 
                  alt={entry.name}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    objectFit: 'cover',
                    border: '2px solid #fff',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    flexShrink: 0
                  }}
                />
              ) : (
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  backgroundColor: COLORS[index % COLORS.length],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '1rem',
                  border: '2px solid #fff',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  flexShrink: 0
                }}>
                  {entry.name.charAt(0).toUpperCase()}
                </div>
              )}

              {/* Washer Info */}
              <div style={{ 
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.25rem'
              }}>
                <span style={{ 
                  fontSize: '0.875rem',
                  color: '#111827',
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {entry.name}
                </span>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem'
                }}>
                  {entry.spLogo && (
                    <img 
                      src={entry.spLogo} 
                      alt={entry.spName}
                      style={{
                        width: '14px',
                        height: '14px',
                        borderRadius: '3px',
                        objectFit: 'cover'
                      }}
                    />
                  )}
                  <span style={{ 
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {entry.spName}
                  </span>
                </div>
              </div>

              {/* Bookings Stats */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: '0.125rem',
                flexShrink: 0
              }}>
                <span style={{ 
                  fontSize: '0.875rem',
                  color: '#111827',
                  fontWeight: 700
                }}>
                  {entry.value.toLocaleString()}
                </span>
                <span style={{ 
                  fontSize: '0.75rem',
                  color: '#6b7280'
                }}>
                  {entry.percentage}%
                </span>
              </div>

              {/* Color Indicator */}
              <div style={{
                width: '4px',
                height: '100%',
                position: 'absolute',
                left: 0,
                top: 0,
                backgroundColor: COLORS[index % COLORS.length],
                borderRadius: '0.5rem 0 0 0.5rem'
              }} />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(TopWashers, (prevProps, nextProps) => {
  return (
    prevProps.dateRange?.startTimestamp === nextProps.dateRange?.startTimestamp &&
    prevProps.dateRange?.endTimestamp === nextProps.dateRange?.endTimestamp &&
    prevProps.loading === nextProps.loading &&
    prevProps.error === nextProps.error &&
    prevProps.dailyAnalytics === nextProps.dailyAnalytics &&
    prevProps.previousDailyAnalytics === nextProps.previousDailyAnalytics &&
    prevProps.filterBySpId === nextProps.filterBySpId
  );
});
